import * as React from 'react';
import { Link, useLocation } from 'react-router';
import { LIVE_GATE } from '@/components/blog/CountdownTimer';

function getRemaining(target: string) {
  const diff = new Date(target).getTime() - Date.now();
  const clamped = Math.max(diff, 0);
  const totalSeconds = Math.floor(clamped / 1000);
  return {
    days: Math.floor(totalSeconds / 86400),
    hours: Math.floor((totalSeconds % 86400) / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
    expired: diff <= 0,
  };
}

/**
 * Sticky top-of-page banner shown on every public page (never the dashboard),
 * pointing at whichever gate is open before it closes. Skips itself on the post's
 * own page — that page already has the full countdown, repeating the banner
 * there would be redundant clutter, not urgency.
 *
 * WHICH gate this points at is not decided here: it reads `LIVE_GATE` from
 * `blog/CountdownTimer.tsx`, beside the deadline it counts down to. The alpha
 * announcement used this same banner via `WAITLIST_CLOSES_AT`; when the alpha closed the
 * banner hid itself (expired), and it reappears for the closed beta from the same code.
 */
export const GateBanner = () => {
  const location = useLocation();
  const [remaining, setRemaining] = React.useState(() => getRemaining(LIVE_GATE.target));

  React.useEffect(() => {
    const id = window.setInterval(() => setRemaining(getRemaining(LIVE_GATE.target)), 1000);
    return () => window.clearInterval(id);
  }, []);

  if (remaining.expired) return null;
  if (location.pathname === LIVE_GATE.postPath) return null;

  return (
    <Link
      to={LIVE_GATE.postPath}
      className="flex items-center justify-center gap-2 sm:gap-3 bg-red-600 hover:bg-red-500 px-4 py-2 text-center text-xs sm:text-sm font-semibold text-white transition-colors"
    >
      <span className="hidden sm:inline">{LIVE_GATE.label}</span>
      <span className="sm:hidden">Closes in</span>
      <span className="font-bold tabular-nums">
        {remaining.days}d {String(remaining.hours).padStart(2, '0')}h{' '}
        {String(remaining.minutes).padStart(2, '0')}m {String(remaining.seconds).padStart(2, '0')}s
      </span>
      <span className="hidden sm:inline">— join before it closes</span>
      <span className="underline underline-offset-2">{LIVE_GATE.cta}</span>
    </Link>
  );
};
